import { useCallback, useEffect, useRef, useState } from 'react';

const useBoardZoom = function(boardRef, setPan) {
  const [zoom, setZoom] = useState(1);

  const cursorRef = useRef(null);
  const zoomTimer = useRef(null);

  const changeZoom = useCallback((value, cursor) => {
    const board = boardRef.current;
    const canvas = board?.querySelector('.boardCanvas');

    if (!canvas) return;

    const previous = Number(board.dataset.zoom) || 1;
    const next = Math.max(0.05, Math.min(3, value));

    if (next === previous) return;

    // Finish any grid animation before changing the canvas scale.
    board.classList.add('isZooming');
    clearTimeout(zoomTimer.current);
    zoomTimer.current = setTimeout(() => board.classList.remove('isZooming'), 150);

    const anchor = cursor || cursorRef.current;
    const x = Math.max(0, Math.min(board.clientWidth, anchor?.x ?? board.clientWidth / 2));
    const y = Math.max(0, Math.min(board.clientHeight, anchor?.y ?? board.clientHeight / 2));
    const panX = Number(board.dataset.panX) || 0;
    const panY = Number(board.dataset.panY) || 0;
    // Keep the same canvas coordinate under the cursor without native scrolling.
    const nextPan = {
      x: x - (x - panX) * next / previous,
      y: y - (y - panY) * next / previous
    };

    canvas.style.zoom = next;
    board.dataset.zoom = next;
    canvas.style.translate = `${nextPan.x / next}px ${nextPan.y / next}px`;
    board.dataset.panX = nextPan.x;
    board.dataset.panY = nextPan.y;
    setPan(nextPan);
    setZoom(next);
  }, [boardRef, setPan]);

  useEffect(() => {
    const board = boardRef.current;

    if (!board) return;

    const trackCursor = function(event) {
      const bounds = board.getBoundingClientRect();

      cursorRef.current = {
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top
      };
    };

    const handleWheel = function(event) {
      if (!event.ctrlKey) return;

      event.preventDefault();

      const previous = Number(board.dataset.zoom) || 1;
      const delta = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? board.clientHeight : 1);
      const bounds = board.getBoundingClientRect();

      changeZoom(previous * Math.exp(-delta * 0.002), {
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top
      });
    };

    board.addEventListener('pointermove', trackCursor);
    board.addEventListener('wheel', handleWheel, {
      passive: false
    });

    return () => {
      clearTimeout(zoomTimer.current);
      board.classList.remove('isZooming');
      board.removeEventListener('pointermove', trackCursor);
      board.removeEventListener('wheel', handleWheel);
    };
  }, [boardRef, changeZoom]);

  return {
    zoom,
    changeZoom
  };
};

export default useBoardZoom;
